/* ==========================================================================
   EIKEN — Retour arrière sur le dernier article retiré            2026-08-28
   Révision 2 : corrections issues de la revue du conseil (5 experts).
   --------------------------------------------------------------------------
   Quelqu'un qui vide son panier hésite souvent, il ne renonce pas toujours :
   c'est presque toujours une question de taille ou de coloris. On mémorise la
   dernière ligne retirée et on propose de la remettre, dans le tiroir comme
   sur la page /cart.

   COMMENT ON ATTRAPE LE RETRAIT
   Par DIFFÉRENCE entre deux états du panier, et non en écoutant le clic. Une
   interception de clic obligeait à retrouver la ligne dans un cache, et ce
   cache pourrissait dès qu'un ajout AJAX passait à côté. La différence est
   déterministe et couvre tous les chemins : corbeille du tiroir, de la page,
   ou quantité ramenée à zéro par le sélecteur.

   CE QUE LA REVUE A CORRIGÉ, ET POURQUOI C'EST ÉCRIT AINSI

   1. ROUTES LOCALISÉES. `/cart.js` sans préfixe renvoie le panier dans la
      langue par défaut. Comme l'état de départ servait de source à la ligne
      mémorisée, le titre du produit sortait EN ANGLAIS dans les 8 langues non
      anglaises, sur le chemin le plus fréquent. On passe par
      `theme.routes.cart` et `theme.routes.cartAdd`, que le thème pose déjà
      dans le <head>.

   2. ÉTAT DE DÉPART SERVI PAR LIQUID. Le snippet écrit le panier en JSON dans
      la page. Plus d'appel réseau au chargement (c'était +1 requête non
      cachable sur CHAQUE page vue du site), et l'état de départ est dans la
      bonne langue.

   3. ON NE COMPARE QUE LES GESTES DU VISITEUR. Comparer au retour sur l'onglet
      faisait lire un panier vidé par un PAIEMENT comme un retrait : la
      boutique proposait de remettre l'article qu'on venait d'acheter, et
      relançait le flux de panier abandonné. `visibilitychange` et l'ouverture
      du tiroir rafraîchissent l'état sans comparer.

   4. LE 422 N'EST PAS UN ÉCHEC. Quand la quantité dépasse le stock, Shopify
      AJOUTE ce qu'il peut ET répond 422. Le code lisait le statut HTTP,
      concluait à l'échec et effaçait tout : panier fantôme de 451 € avec un
      tiroir affichant « vide ». On relit désormais le panier et on décide sur
      le nombre d'articles, jamais sur le code HTTP.

   5. ON DIT QUAND ÇA RATE. Un échec réseau ou un 429 détruisait l'offre en
      silence. On distingue l'indisponible (on oublie, on l'écrit) du
      transitoire (on garde, on réactive le bouton).

   Mémoire de SESSION : l'offre n'a de sens que dans la visite en cours.
   ========================================================================== */
(function () {
  'use strict';

  var CLE = 'eiken:dernier-retrait';
  var DUREE = 30 * 60 * 1000;

  var R = (window.theme && window.theme.routes) || {};
  var URL_CART = R.cart || '/cart.js';
  var URL_ADD = R.cartAdd || '/cart/add.js';

  var panier = null;   // dernier état connu
  var jeton = 0;       // numéro de synchronisation, garde de monotonie
  var minuteur = 0;    // ordonnanceur partagé

  /* ----------------------------------------------------------- mémoire ---- */

  function lire() {
    try {
      var brut = sessionStorage.getItem(CLE);
      if (!brut) return null;
      var d = JSON.parse(brut);
      // Validation stricte : un `ts` absent donnait NaN, et NaN > DUREE vaut
      // false, donc l'entrée n'expirait JAMAIS. Un `ts` dans le futur (horloge
      // client en avance) produisait le même effet.
      if (!d || !d.id || typeof d.ts !== 'number' ||
          d.ts > Date.now() || (Date.now() - d.ts) > DUREE) {
        oublier();
        return null;
      }
      return d;
    } catch (e) {
      oublier(); // une valeur illisible restait en session toute la visite
      return null;
    }
  }

  function ecrire(d) {
    try { sessionStorage.setItem(CLE, JSON.stringify(d)); } catch (e) {}
  }

  function oublier() {
    try { sessionStorage.removeItem(CLE); } catch (e) {}
  }

  /* ------------------------------------------------- état du panier ------- */

  function etat() {
    return fetch(URL_CART, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function parCle(c) {
    var m = {};
    if (c && c.items) {
      for (var i = 0; i < c.items.length; i++) m[c.items[i].key] = c.items[i];
    }
    return m;
  }

  // Quantité totale par variante. Sert de filet au rapprochement par `key`.
  function parVariante(c) {
    var m = {};
    if (c && c.items) {
      for (var i = 0; i < c.items.length; i++) {
        var v = c.items[i].variant_id;
        m[v] = (m[v] || 0) + (c.items[i].quantity || 0);
      }
    }
    return m;
  }

  // Ne retient que les lignes RETIRÉES, c'est-à-dire tombées à zéro. Une baisse
  // partielle (3 -> 1) est un ajustement, pas un renoncement, et la mémoriser
  // écrasait une offre légitime antérieure.
  // Arbitrage sur le PRIX de ligne et non sur la quantité : si le panier est
  // vidé d'un coup, on propose le plus cher, pas le plus nombreux.
  //
  // PIÈGE (corrigé le 2026-09-03) : **Shopify change la `key` d'une ligne quand
  // ses remises changent.** Retirer la housse de pluie fait disparaître
  // l'allocation BXGY « MeroMero Rain Cover -0 » portée par la ligne du SAC, et
  // Shopify re-keye alors le sac :
  //   avant 54350287634777:ae71c764…  ->  après 54350287634777:3ce43fdf…
  // Un rapprochement par `key` seul faisait donc passer le sac pour retiré, et
  // comme il est plus cher que la housse il gagnait l'arbitrage : le bandeau
  // proposait de remettre un article toujours au panier.
  // D'où le second filet sur variant_id. Limite assumée : deux lignes de la MÊME
  // variante distinguées par leurs propriétés (monogramme) dont une seule est
  // retirée ne déclenchent plus d'undo — on préfère ne rien proposer que de
  // proposer le mauvais article.
  function comparer(avant, apres) {
    if (!avant || !avant.items || !avant.items.length) return;
    var neuf = parCle(apres);
    var neufVariantes = parVariante(apres);
    var meilleure = null;
    var valeurMax = -1;

    for (var i = 0; i < avant.items.length; i++) {
      var l = avant.items[i];
      if (neuf[l.key]) continue;                     // toujours au panier
      if (neufVariantes[l.variant_id]) continue;     // re-keyée, pas retirée
      var valeur = l.line_price || l.final_line_price || l.quantity || 0;
      if (valeur > valeurMax) { valeurMax = valeur; meilleure = l; }
    }
    if (!meilleure) return null;

    var retrait = {
      id: meilleure.variant_id || meilleure.id,
      quantity: meilleure.quantity || 1,
      properties: meilleure.properties || null,
      selling_plan: (meilleure.selling_plan_allocation &&
                     meilleure.selling_plan_allocation.selling_plan &&
                     meilleure.selling_plan_allocation.selling_plan.id) || null,
      titre: meilleure.product_title || meilleure.title || '',
      variante: (meilleure.variant_title && meilleure.variant_title !== 'Default Title')
                 ? meilleure.variant_title : '',
      image: meilleure.image || (meilleure.featured_image && meilleure.featured_image.url) || '',
      ts: Date.now()
    };
    ecrire(retrait);

    // La vignette est le seul élément qui fait reconnaître le sac : on la
    // chauffe tout de suite, sans attendre que la carte soit visible.
    if (meilleure.image) { var p = new Image(); p.src = vignette(meilleure.image); }

    return retrait;
  }

  function synchroniser(comparerAussi) {
    var mien = ++jeton;
    return etat().then(function (c) {
      if (!c) return null;
      // Garde de monotonie : une requête partie avant un changement et arrivée
      // après reposait un état périmé, ce qui refaisait mémoriser le même
      // retrait et rejouait l'animation.
      if (mien !== jeton) return null;
      if (comparerAussi) comparer(panier, c);
      panier = c;
      return c;
    });
  }

  // Un seul ordonnanceur pour tous les événements : dix `cart:build` d'affilée
  // déclenchaient dix `/cart.js` simultanés, sans anti-rebond.
  function programmer(comparerAussi, delai) {
    if (minuteur) clearTimeout(minuteur);
    minuteur = setTimeout(function () {
      minuteur = 0;
      synchroniser(comparerAussi).then(rendreTout);
    }, delai === undefined ? 120 : delai);
  }

  /* --------------------------------------------------------- annonces ----- */

  function annoncer(texte) {
    var r = document.querySelector('[data-cart-annonce]');
    if (!r || !texte) return;
    r.textContent = '';
    setTimeout(function () { r.textContent = texte; }, 60);
  }

  /* ------------------------------------------------------------- rendu ---- */

  // Paramètre `width` plutôt qu'un suffixe `_120x` inséré par expression
  // régulière : marche sur toutes les URL du CDN quelle que soit l'extension,
  // et ne peut pas produire un double suffixe sur une URL déjà redimensionnée.
  function vignette(src) {
    if (!src) return '';
    if (src.indexOf('cdn.shopify.com') === -1) return src;
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=156';
  }

  // Deux onglets ouverts : celui resté en arrière-plan gardait sa carte et
  // rajoutait une seconde unité. Si la variante est déjà au panier dans la
  // quantité mémorisée, l'offre n'a plus d'objet.
  function dejaRevenu(d) {
    if (!panier || !panier.items) return false;
    for (var i = 0; i < panier.items.length; i++) {
      var l = panier.items[i];
      if ((l.variant_id || l.id) === d.id && l.quantity >= d.quantity) return true;
    }
    return false;
  }

  function rendre(hote) {
    var d = lire();

    if (d && dejaRevenu(d)) { oublier(); d = null; }

    if (!d) {
      hote.setAttribute('hidden', '');
      hote.innerHTML = '';
      hote.classList.remove('is-leaving');
      hote.removeAttribute('data-ts');
      return;
    }

    // Sans ce garde, `rendreTout` (appelé sur plusieurs événements)
    // reconstruirait une carte identique et rejouerait l'animation d'entrée.
    if (hote.getAttribute('data-ts') === String(d.ts) && !hote.hasAttribute('hidden')) return;

    var libelle = hote.getAttribute('data-label') || 'Put it back';

    hote.innerHTML = '';
    hote.classList.remove('is-leaving');
    hote.setAttribute('data-ts', String(d.ts));

    var img = document.createElement('img');
    img.className = 'eiken-empty__undo-img';
    img.src = vignette(d.image);
    img.alt = '';
    img.width = 52;
    img.height = 52;
    // PAS de `lazy` : la carte est posée dans un conteneur encore masqué, et
    // `lazy` retardait la requête jusqu'à l'affichage. Mesuré : 588 ms de carré
    // gris à la place du sac, sur l'élément dont tout l'intérêt est qu'on
    // reconnaisse son sac.
    img.loading = 'eager';
    img.decoding = 'async';

    var txt = document.createElement('div');
    txt.className = 'eiken-empty__undo-txt';
    var t = document.createElement('span');
    t.className = 'eiken-empty__undo-title';
    t.textContent = d.titre;
    txt.appendChild(t);
    if (d.variante) {
      var v = document.createElement('span');
      v.className = 'eiken-empty__undo-variant';
      v.textContent = d.variante;
      txt.appendChild(v);
    }

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'eiken-empty__undo-btn';
    btn.textContent = libelle;
    // Le libellé seul (« Remettre », « Put it back ») n'a pas d'antécédent dans
    // une liste de boutons de lecteur d'écran. Le nom accessible nomme le sac,
    // et contient le texte visible, ce qui respecte WCAG 2.5.3.
    btn.setAttribute('aria-label', libelle + ' : ' + d.titre + (d.variante ? ', ' + d.variante : ''));
    btn.addEventListener('click', function () { remettre(d, btn, hote); });

    if (d.image) hote.appendChild(img);
    hote.appendChild(txt);
    hote.appendChild(btn);
    hote.removeAttribute('hidden');
  }

  function rendreTout() {
    var hotes = document.querySelectorAll('[data-cart-undo]');
    for (var i = 0; i < hotes.length; i++) rendre(hotes[i]);
  }

  function message(hote, texte) {
    var txt = hote.querySelector('.eiken-empty__undo-txt');
    if (!txt || !texte) return;
    var m = txt.querySelector('.eiken-empty__undo-msg');
    if (!m) {
      m = document.createElement('span');
      m.className = 'eiken-empty__undo-msg';
      txt.appendChild(m);
    }
    m.textContent = texte;
    annoncer(texte);
  }

  /* ---------------------------------------------------------- remise ------ */

  function remettre(d, btn, hote) {
    btn.disabled = true;
    var surPagePanier = document.body.classList.contains('template-cart');
    var avant = (panier && panier.item_count) || 0;

    var ligne = { id: d.id, quantity: d.quantity };
    if (d.properties && Object.keys(d.properties).length) ligne.properties = d.properties;
    if (d.selling_plan) ligne.selling_plan = d.selling_plan;

    var statut = 0;

    fetch(URL_ADD, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [ligne] })
    })
      .then(function (r) { statut = r.status; })
      .catch(function () { statut = 0; })
      // On ne décide JAMAIS sur le code HTTP : Shopify répond 422 tout en ayant
      // ajouté ce qu'il pouvait quand le stock est insuffisant. Seul l'état
      // réel du panier fait foi.
      .then(etat)
      .then(function (apres) {
        if (apres) panier = apres;
        var ajoute = apres && apres.item_count > avant;

        if (ajoute) {
          oublier();
          hote.classList.add('is-leaving');
          annoncer(d.titre);

          if (surPagePanier) {
            // La branche « panier vide » de la page est rendue côté serveur :
            // rien à reconstruire, seul un rechargement affiche le panier plein.
            window.location.reload();
            return;
          }
          // La carte n'est pas retirée du flux ici : la vider tout de suite
          // faisait remonter les trois portes d'un coup pendant que le bloc
          // était encore visible (saut de mise en page mesuré). Le rendu
          // suivant s'en charge, quand le conteneur est déjà masqué.
          setTimeout(function () {
            document.dispatchEvent(new CustomEvent('cart:build', {
              detail: { source: 'eiken-undo' }
            }));
            setTimeout(function () {
              var cible = document.querySelector('#CartDrawer .cart__checkout, #CartDrawer [name="checkout"]');
              if (cible && document.activeElement === document.body) {
                cible.focus({ preventScroll: true });
              }
              rendreTout();
            }, 450);
          }, 190);
          return;
        }

        // Rien n'a été ajouté. Deux familles d'échec, deux réponses.
        if (statut === 422) {
          // Épuisé ou dépublié depuis le retrait : l'offre n'a plus d'objet.
          oublier();
          hote.removeAttribute('data-ts');
          message(hote, (window.theme && theme.strings && theme.strings.soldOut) || '');
          if (btn.parentNode) btn.parentNode.removeChild(btn);
        } else {
          // Réseau, 429, 5xx : transitoire. On garde l'offre et on laisse
          // réessayer, au lieu de détruire la seule chose qui rattrape la vente.
          btn.disabled = false;
          message(hote, hote.getAttribute('data-msg-erreur') || '');
        }
      });
  }

  /* ------------------------------------------------ derniers regardes ----- */

  // Le bloc « vus récemment » du tiroir est marqué `data-lazy` : le module ne le
  // rend que sur demande. Sans ça, il irait chercher une carte produit par
  // article d'historique sur CHAQUE page du site, puisque le tiroir y est
  // toujours présent, pour un bloc que la plupart des visiteurs n'ouvriront
  // jamais. On ne le demande donc que quand le panier est réellement vide.
  // PAS de garde « une seule fois » ici, et c'est le fruit d'un bug observé :
  // avec un drapeau, la toute première demande partait parfois AVANT que
  // `recently-viewed.js` n'ait enregistré son écouteur. L'événement tombait
  // dans le vide, le drapeau restait levé, et le bloc n'était plus jamais
  // rendu de la visite. On redemande donc à chaque occasion ; c'est le module
  // qui garde le compte, avec son `data-rendu` par hôte.
  function montrerVus() {
    if (panier && panier.item_count > 0) return;
    document.dispatchEvent(new CustomEvent('eiken:recently-viewed:show'));
  }

  /* --------------------------------------------- bandeau de rattrapage ---- */

  // Après un retrait qui ne vide PAS le panier, un bandeau « titre + Remettre »
  // s'affiche quelques secondes sous l'en-tête du tiroir. C'est le filet du
  // retrait optimiste : la carte « remettre » de l'état vide ne couvre que le
  // panier vidé, et la revue avait pointé qu'en panier plein la suppression
  // était sans recours. Aucun des 11 sites du benchmark n'a cet undo.
  var bandeauMinuteur = 0;
  var sortieMinuteur = 0;

  // Sortie animée (`.is-leaving`, keyframes eiken-empty-sortie de 200 ms, CSS
  // dans cart-drawer.liquid) avant de masquer. `immediat` : sans fondu, quand
  // le panier se vide et que l'état vide prend la place (un fondu y serait
  // invisible et retarderait le nettoyage).
  function cacherBandeau(immediat) {
    if (bandeauMinuteur) { clearTimeout(bandeauMinuteur); bandeauMinuteur = 0; }
    if (sortieMinuteur) { clearTimeout(sortieMinuteur); sortieMinuteur = 0; }
    var strip = document.querySelector('[data-undo-strip]');
    if (!strip) return;
    function fin() {
      sortieMinuteur = 0;
      strip.classList.remove('is-leaving');
      strip.setAttribute('hidden', '');
      strip.innerHTML = '';
    }
    if (immediat === true || strip.hasAttribute('hidden')) { fin(); return; }
    strip.classList.add('is-leaving');
    sortieMinuteur = setTimeout(fin, 200);
  }

  function montrerBandeau(d) {
    var strip = document.querySelector('[data-undo-strip]');
    if (!strip || !d) return;

    // Nouveau retrait pendant la sortie du précédent : on l'annule, sinon le
    // minuteur de sortie masquerait le bandeau qu'on est en train d'afficher.
    if (sortieMinuteur) { clearTimeout(sortieMinuteur); sortieMinuteur = 0; }
    strip.classList.remove('is-leaving');

    strip.innerHTML = '';
    var txt = document.createElement('span');
    txt.className = 'drawer__undo-strip-txt';
    txt.textContent = d.titre;

    var libelle = strip.getAttribute('data-label') || 'Put it back';
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'drawer__undo-strip-btn';
    btn.textContent = libelle;
    btn.setAttribute('aria-label', libelle + ' : ' + d.titre + (d.variante ? ', ' + d.variante : ''));
    btn.addEventListener('click', function () {
      btn.disabled = true;
      var avant = (panier && panier.item_count) || 0;
      var ligne = { id: d.id, quantity: d.quantity };
      if (d.properties && Object.keys(d.properties).length) ligne.properties = d.properties;
      if (d.selling_plan) ligne.selling_plan = d.selling_plan;

      fetch(URL_ADD, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [ligne] })
      })
        .catch(function () {})
        // Même principe que la carte de l'état vide : on ne conclut jamais sur
        // le code HTTP, seul l'état relu du panier fait foi.
        .then(etat)
        .then(function (apres) {
          if (apres) panier = apres;
          if (apres && apres.item_count > avant) {
            oublier();
            cacherBandeau();
            annoncer(d.titre);
            document.dispatchEvent(new CustomEvent('cart:build', { detail: { source: 'eiken-undo' } }));
          } else {
            btn.disabled = false; // transitoire : on laisse réessayer
          }
        });
    });

    strip.appendChild(txt);
    strip.appendChild(btn);
    strip.removeAttribute('hidden');
    annoncer(d.titre);

    relancerMinuteur();
    // 19/10/2026 : le bandeau ne part plus pendant qu'on s'en sert. Survol,
    // focus clavier ou doigt posé suspendent les 8 s ; elles repartent à la
    // sortie (WCAG 2.2.1, réglage du temps). Posé une seule fois par bandeau :
    // le nœud reste le même d'un retrait à l'autre.
    if (!strip.__eikenPause) {
      strip.__eikenPause = true;
      var suspendre = function () {
        if (bandeauMinuteur) { clearTimeout(bandeauMinuteur); bandeauMinuteur = 0; }
      };
      // Reprend sauf si un focus CLAVIER est encore dans le bandeau (le focus posé par
      // focusApresRetrait après un tap ne compte pas, sinon souris et doigt bloquaient tout).
      var reprendre = function () {
        if (strip.hasAttribute('hidden')) return;
        var ae = document.activeElement;
        var clavier = false;
        if (ae && strip.contains(ae)) {
          try { clavier = ae.matches(':focus-visible'); } catch (err) { clavier = true; }
        }
        if (!clavier) relancerMinuteur();
      };
      // Survol : vraie souris seulement (revue du 01/11/2026). Après un toucher, Safari iOS
      // émet un mouseenter simulé, sans mouseleave : il re-suspendait le minuteur pour de bon.
      strip.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') suspendre(); });
      // 01/11/2026 : focusApresRetrait pose lui-même le focus sur « Remettre » ; ce focus
      // suspendait les 8 s pour de bon et le bandeau ne partait plus après un tap. La pause
      // ne vaut que pour un focus clavier (:focus-visible), celui qui a besoin de temps.
      strip.addEventListener('focusin', function (e) {
        var t = e.target;
        var clavier = true;
        try { clavier = !!(t && t.matches && t.matches(':focus-visible')); } catch (err) {}
        if (clavier) suspendre();
      });
      strip.addEventListener('touchstart', suspendre, { passive: true });
      strip.addEventListener('touchend', function () { setTimeout(reprendre, 0); }, { passive: true });
      strip.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') reprendre(); });
      strip.addEventListener('focusout', function () { setTimeout(reprendre, 0); });
    }
  }

  function relancerMinuteur() {
    if (bandeauMinuteur) clearTimeout(bandeauMinuteur);
    bandeauMinuteur = setTimeout(cacherBandeau, 8000);
  }

  /* ----------------------------------------------------------- focus ------ */

  // Le thème vide `products.innerHTML` en reconstruisant le panier : le bouton
  // corbeille qui avait le focus est détruit et le focus retombe sur <body>.
  // Au clavier, la frappe suivante repart du haut du document ; au lecteur
  // d'écran, plus rien n'est annoncé.
  function replacerFocus() {
    if (document.activeElement && document.activeElement !== document.body) return;
    var racine = document.querySelector('#CartDrawer .eiken-empty') ||
                 document.querySelector('.eiken-empty');
    if (racine && racine.focus) racine.focus({ preventScroll: true });
  }

  // Panier encore plein après un retrait (19/10/2026) : le focus tombait aussi
  // sur <body>, la reconstruction ayant détruit la corbeille. On le pose sur
  // « Remettre », l'action la plus probable juste après un retrait ; à défaut,
  // sur le titre du tiroir.
  function focusApresRetrait() {
    if (document.activeElement && document.activeElement !== document.body) return;
    var tiroir = document.getElementById('CartDrawer');
    var cible = null;
    if (tiroir && tiroir.classList.contains('drawer--is-open')) {
      cible = tiroir.querySelector('[data-undo-strip]:not([hidden]) .drawer__undo-strip-btn') ||
              tiroir.querySelector('#CartDrawerTitle');
    } else {
      // Page /cart (20/10/2026) : même règle, « Remettre » d'abord, sinon le titre de la page.
      var page = document.querySelector('.eiken-cart');
      if (!page) return;
      cible = page.querySelector('[data-undo-strip]:not([hidden]) .drawer__undo-strip-btn') ||
              page.querySelector('.eiken-cart__title');
    }
    if (cible && cible.focus) cible.focus({ preventScroll: true });
  }

  /* ----------------------------------------------------------- cycle ------ */

  function etatInitial() {
    // Servi par Liquid, donc déjà dans la bonne langue, et sans requête.
    var n = document.querySelector('[data-cart-state]');
    if (!n) return null;
    try { return JSON.parse(n.textContent); } catch (e) { return null; }
  }

  function init() {
    if (!document.querySelector('[data-cart-undo]')) return; // ni tiroir ni page

    // Les écouteurs d'abord : si quoi que ce soit échouait plus bas, ils
    // seraient quand même en place.

    // `cart:updated` transporte le panier complet dans son `detail`
    // (theme.js:1744). On s'en sert tel quel : aucune attente, aucun appel
    // réseau, et la source est localisée par le thème.
    document.addEventListener('cart:updated', function (e) {
      var c = e && e.detail && e.detail.cart;
      if (c && c.items) {
        var retrait = comparer(panier, c);
        panier = c;
        rendreTout();
        if (retrait && c.item_count > 0) {
          montrerBandeau(retrait);
          // 700 ms : la ligne retirée (encore dans le DOM pendant son animation)
          // garde le focus jusqu'à la reconstruction du thème, mesurée vers 630 ms.
          setTimeout(focusApresRetrait, 700);
        }
        if (c.item_count === 0) {
          cacherBandeau(true);
          var phrase = document.querySelector('.eiken-empty__line');
          annoncer(phrase ? phrase.textContent : '');
          montrerVus();
          // Après la bascule en état vide, mesurée autour de 630 ms.
          setTimeout(replacerFocus, 700);
        }
      } else {
        programmer(true);
      }
    });

    ['ajaxProduct:added', 'cart:build'].forEach(function (nom) {
      document.addEventListener(nom, function (e) {
        // Ne pas répondre à notre propre événement de reconstruction.
        if (e && e.detail && e.detail.source === 'eiken-undo') return;
        programmer(true);
      });
    });

    // Ajout depuis un panier VIDE : le thème ouvre le tiroir dès `ajaxProduct:added`,
    // environ 330 ms AVANT d'avoir reconstruit son contenu (mesuré le 15/10/2026).
    // On voyait l'état vide (lynx, portes) glisser, puis basculer d'un coup sur le
    // panier plein. Tant que la reconstruction n'est pas là, `.is-filling` masque
    // l'état vide ; à son arrivée, `.is-filled` fait entrer le contenu en fondu
    // (CSS dans cart-drawer.liquid). Filet à 2,5 s si elle n'arrive jamais (erreur
    // d'ajout) : l'état vide réapparaît tel quel.
    document.addEventListener('ajaxProduct:added', function () {
      var tiroir = document.getElementById('CartDrawer');
      if (!tiroir) return;
      var vide = tiroir.classList.contains('is-empty') || (panier && panier.item_count === 0);
      if (!vide) return;
      tiroir.classList.add('is-filling');
      var debut = Date.now();
      var sonde = setInterval(function () {
        var pret = !tiroir.classList.contains('is-empty') && tiroir.querySelector('.cart__item');
        if (!pret && Date.now() - debut < 2500) return;
        clearInterval(sonde);
        tiroir.classList.remove('is-filling');
        if (!pret) return;
        tiroir.classList.add('is-filled');
        setTimeout(function () { tiroir.classList.remove('is-filled'); }, 400);
      }, 40);
    });

    // Ouverture du tiroir et retour sur l'onglet : on RAFRAÎCHIT, on ne compare
    // PAS. Comparer ici lisait un panier vidé par un paiement, une expiration
    // ou un autre appareil comme un retrait volontaire.
    // CAPTURE obligatoire. Le thème appelle `evt.stopPropagation()` en ouvrant le
    // tiroir (theme.js:2195, commenté « sinon le clic remonte jusqu'à la page et
    // referme le tiroir »). Un écouteur en phase de bouillonnement ne voit donc
    // JAMAIS ce clic. Le piège est vicieux : `open()` sort avant le
    // `stopPropagation` quand le tiroir est déjà ouvert, si bien qu'un second
    // clic passe. En test, le déclencheur semblait donc fonctionner une fois
    // sur deux, et le bloc « vus récemment » ne se remplissait qu'au deuxième
    // essai. En capture, on passe avant le gestionnaire du thème.
    document.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('.js-drawer-open-cart')) {
        programmer(false, 400);
        // On ne teste PAS `is-empty` sur le tiroir : sur un chargement neuf, le
        // thème ne pose cette classe qu'après avoir reconstruit le panier, soit
        // bien après 450 ms. Le test échouait donc silencieusement à la
        // première ouverture, celle qui compte. `montrerVus` s'appuie sur notre
        // propre état, rafraîchi par le `programmer` ci-dessus.
        setTimeout(montrerVus, 520);
      }
    }, true);

    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) programmer(false, 0);
    });

    // État de départ : Liquid d'abord, réseau seulement en dernier recours.
    panier = etatInitial();
    if (panier) rendreTout();
    else synchroniser(false).then(rendreTout);

    // Page /cart servie avec un panier déjà vide : le bloc est visible tout de
    // suite, il faut donc le remplir sans attendre un geste.
    if (panier && panier.item_count === 0 &&
        document.querySelector('.eiken-empty--page [data-recently-viewed]')) {
      // `setTimeout(0)` et non un appel direct : les scripts différés
      // s'exécutent à la suite, sans rendre la main à la boucle d'événements.
      // Émis ici même, l'événement partirait avant que `recently-viewed.js`
      // n'ait enregistré son écouteur, et le bloc de la page /cart ne se
      // remplirait jamais, faute d'un second déclencheur sur ce gabarit.
      setTimeout(montrerVus, 0);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();


/* ==========================================================================
   EIKEN — Retrait optimiste                                       2026-08-31
   --------------------------------------------------------------------------
   Le thème retire l'article côté serveur, attend la réponse, puis reconstruit
   tout le panier. Mesuré sur le site : 661 ms entre le clic sur la corbeille
   et le premier changement à l'écran. On fait donc partir la ligne tout de
   suite, et on laisse le thème faire son travail derrière.

   MÊME CONDITION QUE LE THÈME, volontairement. `theme.js:1598` teste
   `e.target.matches('.cart__remove')`, une correspondance exacte. Si on
   utilisait `closest`, on ferait disparaître des lignes que le thème, lui, ne
   retirerait pas : l'article resterait au panier alors que l'écran dirait le
   contraire. On préfère ne rien faire là où le thème ne fait rien.
   (Le clic perdu sur l'icône est corrigé séparément, en CSS, par
   `.cart__remove * { pointer-events: none }` qui n'existait que dans le tiroir.)

   FILET : si aucun `cart:updated` n'arrive dans les 4 secondes, l'appel a
   échoué et la ligne est remise en place. Sans ce filet, une coupure réseau
   laisserait un panier qui paraît vidé et ne l'est pas.
   ========================================================================== */
(function () {
  'use strict';

  var DUREE_FILET = 4000;

  function partir(ligne) {
    if (ligne.classList.contains('eiken-is-removing')) return;

    // Hauteur figée avant l'animation : on ne peut pas transitionner depuis
    // `auto`. La lecture force le calcul de style, sinon le navigateur groupe
    // les deux écritures et rien ne s'anime.
    ligne.style.height = ligne.getBoundingClientRect().height + 'px';
    void ligne.offsetHeight;

    ligne.classList.add('eiken-is-removing');
    requestAnimationFrame(function () { ligne.style.height = '0px'; });

    // Dernière ligne du tiroir : le thème ne bascule en état vide qu'à l'arrivée
    // du nouveau contenu (~500 ms, theme.js cartMarkup), et le bas du tiroir
    // (total, Commander, paiements) disparaissait alors d'un coup. On l'efface en
    // même temps que la ligne (.eiken-is-emptying, eiken-cart-empty.css) ; la
    // classe tombe dès que `is-empty` est posé, ou avec le filet.
    // Tiroir, ou page /cart (.eiken-cart reçoit `is-empty` de Motion : this.wrapper = form.parentNode).
    var tiroir = ligne.closest ? (ligne.closest('#CartDrawer') || ligne.closest('.eiken-cart')) : null;
    var vidage = 0;
    if (tiroir && !tiroir.querySelector('.cart__item:not(.eiken-is-removing)')) {
      tiroir.classList.add('eiken-is-emptying');
      vidage = setInterval(function () {
        if (!tiroir.classList.contains('is-empty')) return;
        clearInterval(vidage);
        tiroir.classList.remove('eiken-is-emptying');
      }, 40);
    }

    var filet = setTimeout(function () {
      document.removeEventListener('cart:updated', abandonner);
      ligne.classList.remove('eiken-is-removing');
      ligne.style.height = '';
      if (vidage) { clearInterval(vidage); tiroir.classList.remove('eiken-is-emptying'); }
      signalerEchec(ligne);
    }, DUREE_FILET);

    function abandonner() {
      clearTimeout(filet);
      document.removeEventListener('cart:updated', abandonner);
      // Rien à nettoyer : le thème remplace tout le contenu de la liste.
    }
    document.addEventListener('cart:updated', abandonner);
  }

  // La ligne revenait en place sans un mot (19/10/2026) : on dit que le retrait
  // n'a pas abouti, dans la ligne même, annoncé aux lecteurs d'écran. Le texte
  // vient de cart-drawer.liquid (data-remove-error sur le bandeau « Remettre »).
  function signalerEchec(ligne) {
    var src = document.querySelector('[data-undo-strip][data-remove-error]');
    var texte = (src && src.getAttribute('data-remove-error')) || 'We couldn\u2019t remove this item. Please try again.';
    var nom = ligne.querySelector('.cart__item--name') || ligne;
    var msg = nom.querySelector('.cart__line-error');
    if (!msg) {
      msg = document.createElement('p');
      msg.className = 'cart__line-error';
      msg.setAttribute('role', 'alert');
      nom.appendChild(msg);
    }
    msg.textContent = texte;
  }

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.matches || !e.target.matches('.cart__remove')) return;
    var ligne = e.target.closest ? e.target.closest('.cart__item') : null;
    if (ligne) partir(ligne);
  }, true); // capture : avant le gestionnaire du thème
})();


/* ==========================================================================
   EIKEN — Focus gardé dans le tiroir ouvert                       2026-10-19
   --------------------------------------------------------------------------
   Mesuré le 19/10 : après « Commander », Tab partait vers le logo de l'en-tête,
   derrière le voile. Le tiroir porte role="dialog" aria-modal="true"
   (cart-drawer.liquid) : le focus doit y rester tant qu'il est ouvert (WCAG
   2.4.3). Échap et la fermeture restent ceux du thème.
   ========================================================================== */
(function () {
  'use strict';

  var SELECTEUR = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
                  'select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  function visible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length) &&
           getComputedStyle(el).visibility !== 'hidden';
  }

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab') return;
    var tiroir = document.getElementById('CartDrawer');
    if (!tiroir || !tiroir.classList.contains('drawer--is-open')) return;
    var cibles = Array.prototype.filter.call(tiroir.querySelectorAll(SELECTEUR), visible);
    if (!cibles.length) return;
    var premier = cibles[0], dernier = cibles[cibles.length - 1];
    var actif = document.activeElement;
    if (!tiroir.contains(actif)) {
      e.preventDefault();
      (e.shiftKey ? dernier : premier).focus();
    } else if (!e.shiftKey && actif === dernier) {
      e.preventDefault();
      premier.focus();
    } else if (e.shiftKey && actif === premier) {
      e.preventDefault();
      dernier.focus();
    }
  });
})();


/* ==========================================================================
   EIKEN — Code promo masqué quand tout le panier est déjà remisé  2026-10-20
   --------------------------------------------------------------------------
   Chaque ligne d'Offres d'automne ou d'outlet porte `data-no-code`
   (cart-item.liquid) et dit déjà « non éligible aux codes promo ». Quand TOUTES
   les lignes la portent, l'accordéon « Vous avez un code promo ? » se masque
   (.is-codes-off, CSS dans cart-drawer.liquid) ; un code déjà posé reste
   visible pour pouvoir le retirer (.is-applied gagne). Rendu initial côté
   Liquid ; ici on suit les reconstructions du thème.
   ========================================================================== */
(function () {
  'use strict';

  function recalculer() {
    // Tiroir, ou page /cart (le tiroir n'y est plus rendu depuis le 20/10/2026).
    var tiroir = document.getElementById('CartDrawer') || document.querySelector('.eiken-cart');
    var bloc = tiroir && tiroir.querySelector('[data-discount-wrapper]');
    if (!bloc) return;
    var lignes = tiroir.querySelectorAll('[data-products] .cart__item:not(.eiken-is-removing)');
    var sansCode = tiroir.querySelectorAll('[data-products] .cart__item[data-no-code]:not(.eiken-is-removing)');
    bloc.classList.toggle('is-codes-off', lignes.length > 0 && sansCode.length === lignes.length);
  }

  function brancher() {
    var produits = document.querySelector('#CartDrawer [data-products], .eiken-cart [data-products]');
    if (!produits) return;
    new MutationObserver(recalculer).observe(produits, { childList: true });
    recalculer();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', brancher);
  else brancher();
})();
